import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';

export default function Transactions() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens, user } = useContext(AuthContext);
  const { selectedPractice } = usePractice();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [selected, setSelected] = useState(null);
  const [invoiceLinkLoading, setInvoiceLinkLoading] = useState(false);
  const [invoiceLinkError, setInvoiceLinkError] = useState('');
  const [invoiceLinkUrl, setInvoiceLinkUrl] = useState('');
  const [invoiceQrFullscreen, setInvoiceQrFullscreen] = useState(false);
  const [highlightTxnId, setHighlightTxnId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const flashedRef = useRef(false);
  const invoiceQrFullscreenRef = useRef(null);
  const [paymentFilter, setPaymentFilter] = useState(null); // 'paid' | 'unpaid' | null

  const shortId = (val) => {
    if (!val) return '';
    const s = String(val);
    return s.length > 8 ? `${s.slice(0, 8)}…` : s;
  };

  const paymentStatusFor = (inv) => {
    if (!inv) return '—';
    const balance = inv?.balance_amount != null ? Number(inv.balance_amount) : null;
    if (balance === 0) return 'Paid';
    return inv.payments?.length ? 'Partial' : 'Unpaid';
  };

  const formatTxnType = (t) => {
    const map = {
      kit_restock_qb_invoice: 'Kit Restock',
    };
    return map[t] || t;
  };

  const requiresOrg = user?.role === 'Personnel';
  const orgId = selectedPractice?.id;

  const fetchData = async () => {
    if (!tokens?.id_token) return;
    if (requiresOrg && !orgId) return; // wait for selection
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('skip', String(page * rowsPerPage));
      params.append('limit', String(rowsPerPage));
      params.append('sort_by', orderBy);
      params.append('sort_order', order);
      if (orgId) params.append('organization_id', orgId);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      const url = `${apiBase}/transactions?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tokens.id_token}` } });
      if (!res.ok) throw new Error('Failed to fetch transactions');
      const data = await res.json();
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message || 'Error fetching transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tokens, user, orgId, page, rowsPerPage, order, orderBy, paymentFilter]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setInvoiceQrFullscreen(document.fullscreenElement === invoiceQrFullscreenRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  // After rows load, check for a query param indicating which invoice to highlight
  useEffect(() => {
    if (flashedRef.current) return;
    if (!rows || rows.length === 0) return;
    const params = new URLSearchParams(location.search);
    const targetInvoiceId = params.get('invoice_id');
    const targetRealmId = params.get('realm_id');
    if (!targetInvoiceId || !targetRealmId) return;
    const match = rows.find(r => r.qb_invoice && r.qb_invoice.invoice_id === targetInvoiceId && r.qb_invoice.realm_id === targetRealmId);
    if (match) {
      setHighlightTxnId(match.id);
      flashedRef.current = true;
      // Clear the query params so it only flashes once
      navigate('/transactions', { replace: true });
      // Remove highlight after a short delay
      setTimeout(() => setHighlightTxnId(null), 2000);
    }
  }, [rows, location.search, navigate]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const fetchInvoiceLink = async () => {
    const inv = selected?.qb_invoice;
    if (!inv?.realm_id || !inv?.invoice_id || !tokens?.id_token) return;
    setInvoiceLinkLoading(true);
    setInvoiceLinkError('');
    setInvoiceLinkUrl('');
    try {
      const res = await fetch(
        `${apiBase}/qb/invoices/${encodeURIComponent(inv.realm_id)}/${encodeURIComponent(inv.invoice_id)}/payment-link`,
        { headers: { Authorization: `Bearer ${tokens.id_token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.invoice_url) {
        throw new Error(data?.detail || 'Unable to retrieve invoice URL');
      }
      setInvoiceLinkUrl(data.invoice_url);
    } catch (e) {
      setInvoiceLinkError(e?.message || 'Unable to retrieve invoice URL');
    } finally {
      setInvoiceLinkLoading(false);
    }
  };

  const openQrFullscreen = async () => {
    if (!invoiceQrFullscreenRef.current) return;
    try {
      if (invoiceQrFullscreenRef.current.requestFullscreen) {
        await invoiceQrFullscreenRef.current.requestFullscreen();
      }
      setInvoiceQrFullscreen(true);
    } catch (err) {
      console.error('Failed to enter full screen QR view:', err);
    }
  };

  if (requiresOrg && !orgId) {
    return (
      <Container disableGutters sx={{ mt: 2 }}>
        <Typography variant="body1">Please select a practice to view transactions.</Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress />
      </Container>
    );
  }
  if (error) {
    return (
      <Container disableGutters sx={{ mt: 2 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Transactions
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
        <ToggleButtonGroup
          value={paymentFilter || 'all'}
          exclusive
          onChange={(e, val) => { setPaymentFilter(val === 'all' ? null : val); setPage(0); }}
          size="small"
          aria-label="payment status filter"
        >
          <ToggleButton value={'all'} aria-label="all">All</ToggleButton>
          <ToggleButton value="paid" aria-label="paid">Paid</ToggleButton>
          <ToggleButton value="unpaid" aria-label="unpaid">Unpaid</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'created_at' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'desc'}
                  onClick={() => handleRequestSort('created_at')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell sortDirection={orderBy === 'total_items' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'total_items'}
                  direction={orderBy === 'total_items' ? order : 'desc'}
                  onClick={() => handleRequestSort('total_items')}
                >
                  Items
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'total_amount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'total_amount'}
                  direction={orderBy === 'total_amount' ? order : 'desc'}
                  onClick={() => handleRequestSort('total_amount')}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>Balance</TableCell>
              <TableCell>Payment Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const inv = r.qb_invoice;
              const balance = inv?.balance_amount != null ? Number(inv.balance_amount) : null;
              const status = inv
                ? (balance === 0 ? 'Paid' : (inv.payments?.length ? 'Partial' : 'Unpaid'))
                : '—';
              return (
                <TableRow
                  key={r.id}
                  hover
                  onClick={() => setSelected(r)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: r.id === highlightTxnId ? '#fff9c4' : undefined,
                    transition: 'background-color 0.4s ease-in-out',
                  }}
                >
                  <TableCell>{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</TableCell>
                  <TableCell>{formatTxnType(r.transaction_type)}</TableCell>
                  <TableCell>{inv?.invoice_number || '—'}</TableCell>
                  <TableCell>{r.total_items ?? ''}</TableCell>
                  <TableCell>{r.total_amount != null ? `$${Number(r.total_amount).toFixed(2)}` : ''}</TableCell>
                  <TableCell>{balance != null ? `$${balance.toFixed(2)}` : '—'}</TableCell>
                  <TableCell>{status}</TableCell>
                </TableRow>
              );
            })}
      </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
      />
      {/* Details Dialog */}
      <Dialog open={Boolean(selected)} onClose={() => {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
        setSelected(null);
        setInvoiceLinkUrl('');
        setInvoiceLinkError('');
        setInvoiceQrFullscreen(false);
      }} maxWidth="md" fullWidth>
        <DialogTitle>Transaction Details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              {/* Top card: transaction details */}
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Transaction</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <strong>ID:</strong> {shortId(selected.id)}
                      <Tooltip title="Copy full ID" arrow>
                        <IconButton
                          aria-label="copy transaction id"
                          size="small"
                          onClick={() => navigator.clipboard.writeText(selected.id)}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm={6}><strong>Type:</strong> {formatTxnType(selected.transaction_type)}</Grid>
                    <Grid item xs={12} sm={6}><strong>Organization:</strong> {selected.organization_id}</Grid>
                    <Grid item xs={12} sm={6}><strong>Created:</strong> {selected.created_at ? new Date(selected.created_at).toLocaleString() : ''}</Grid>
                    <Grid item xs={12} sm={6}><strong>Total Items:</strong> {selected.total_items ?? '—'}</Grid>
                    <Grid item xs={12} sm={6}><strong>Total Amount:</strong> {selected.total_amount != null ? `$${Number(selected.total_amount).toFixed(2)}` : '—'}</Grid>
                    <Grid item xs={12} sm={6}><strong>Payment Status:</strong> {paymentStatusFor(selected.qb_invoice)}</Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Bottom card: QB Invoice details */}
              <Card variant="outlined" sx={{ mb: 1 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Invoice</Typography>
                  {selected.qb_invoice ? (
                    <Box>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}><strong>Invoice #:</strong> {selected.qb_invoice.invoice_number}</Grid>
                        <Grid item xs={12} sm={6}><strong>Invoice ID:</strong> {selected.qb_invoice.invoice_id}</Grid>
                        <Grid item xs={12} sm={6}><strong>Date:</strong> {selected.qb_invoice.invoice_created_time ? new Date(selected.qb_invoice.invoice_created_time).toLocaleString() : ''}</Grid>
                        <Grid item xs={12} sm={6}><strong>Currency:</strong> {selected.qb_invoice.currency_code || '—'}</Grid>
                        <Grid item xs={12} sm={4}><strong>Total:</strong> {selected.qb_invoice.total_amount != null ? `$${Number(selected.qb_invoice.total_amount).toFixed(2)}` : '—'}</Grid>
                        <Grid item xs={12} sm={4}><strong>Balance:</strong> {selected.qb_invoice.balance_amount != null ? `$${Number(selected.qb_invoice.balance_amount).toFixed(2)}` : '—'}</Grid>
                        <Grid item xs={12} sm={4}><strong>Status:</strong> {Number(selected.qb_invoice.balance_amount || 0) === 0 ? 'Paid' : (selected.qb_invoice.payments?.length ? 'Partial' : 'Unpaid')}</Grid>
                        <Grid item xs={12} sm={6}>
                          <strong>Discounts:</strong> {selected.qb_invoice.discount_amount != null ? `-$${Number(selected.qb_invoice.discount_amount).toFixed(2)}` : '—'}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <strong>Tax:</strong> {selected.qb_invoice.tax_amount != null ? `$${Number(selected.qb_invoice.tax_amount).toFixed(2)}` : '—'}
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={fetchInvoiceLink}
                            disabled={invoiceLinkLoading}
                          >
                            {invoiceLinkLoading ? 'Loading Invoice URL...' : 'Show Invoice URL and QR Code'}
                          </Button>
                        </Grid>
                      </Grid>
                      {invoiceLinkError && (
                        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                          {invoiceLinkError}
                        </Typography>
                      )}
                      {invoiceLinkUrl && (
                        <Box sx={{ mb: 2 }}>
                          <Box
                            ref={invoiceQrFullscreenRef}
                            sx={{
                              position: invoiceQrFullscreen ? 'fixed' : 'relative',
                              inset: invoiceQrFullscreen ? 0 : 'auto',
                              zIndex: invoiceQrFullscreen ? 9999 : 'auto',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: invoiceQrFullscreen ? '100vw' : 'fit-content',
                              height: invoiceQrFullscreen ? '100vh' : 'auto',
                              backgroundColor: invoiceQrFullscreen ? '#000' : 'transparent',
                              gap: 2,
                            }}
                          >
                            {invoiceQrFullscreen && (
                              <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                                <IconButton
                                  aria-label="Close full screen QR view"
                                  onClick={() => {
                                    if (document.fullscreenElement) {
                                      document.exitFullscreen().catch(() => {});
                                    } else {
                                      setInvoiceQrFullscreen(false);
                                    }
                                  }}
                                  sx={{ backgroundColor: 'rgba(255,255,255,0.85)' }}
                                >
                                  X
                                </IconButton>
                              </Box>
                            )}
                            <Box
                              component="img"
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(invoiceLinkUrl)}`}
                              alt="Invoice QR code"
                              sx={{ width: 220, height: 220, border: '1px solid #ddd', borderRadius: 1, p: 1, bgcolor: 'white' }}
                            />
                            {invoiceQrFullscreen && (
                              <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center', px: 2 }}>
                                Hold this screen up for the payer to scan. Keep bystanders away for privacy.
                              </Typography>
                            )}
                          </Box>
                          {!invoiceQrFullscreen && (
                            <>
                              <Typography variant="body2" sx={{ mt: 1, mb: 1, wordBreak: 'break-all' }}>
                                <strong>Invoice URL:</strong> {invoiceLinkUrl}
                              </Typography>
                              <Button variant="contained" size="small" onClick={openQrFullscreen}>
                                Full Screen
                              </Button>
                            </>
                          )}
                        </Box>
                      )}
                      <Divider textAlign="left" sx={{ mb: 1 }}>Lines</Divider>
                      {selected.qb_invoice.lines && selected.qb_invoice.lines.length > 0 ? (
                        <Table size="small" sx={{ mb: 2 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Line #</TableCell>
                              <TableCell>Item</TableCell>
                              <TableCell>Quantity</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selected.qb_invoice.lines.map((ln) => (
                              <TableRow key={ln.id}>
                                <TableCell>{ln.line_number || ln.line_id}</TableCell>
                                <TableCell>{ln.item_name}</TableCell>
                                <TableCell>{ln.quantity ?? '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" sx={{ mb: 2 }}>No line items available.</Typography>
                      )}
                      <Divider textAlign="left" sx={{ mb: 1 }}>Payments</Divider>
                      {selected.qb_invoice.payments && selected.qb_invoice.payments.length > 0 ? (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Payment ID</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selected.qb_invoice.payments.map((p) => (
                              <TableRow key={p.id}>
                                <TableCell>{p.payment_id}</TableCell>
                                <TableCell>{p.payment_create_time ? new Date(p.payment_create_time).toLocaleDateString() : ''}</TableCell>
                                <TableCell align="right">{p.amount != null ? `$${Number(p.amount).toFixed(2)}` : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2">No payments recorded.</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2">No invoice associated with this transaction.</Typography>
                  )}
                </CardContent>
              </Card>

              <Box sx={{ textAlign: 'right' }}>
                <Button onClick={() => setSelected(null)} variant="contained">Close</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
